const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes cache
const MAX_RETRIES = 3;
const RETRY_DELAY = 1000; // 1 second

const fetchUrlConfig = async () => {
  try {
    const response = await fetch('/api/config/auth');
    if (!response.ok) {
      throw new Error(`Failed to fetch grafana url: ${response.status}`);
    }
    const config = await response.json();
    const webAPIUri = config.msal.webAPIUri;
    return webAPIUri;

  } catch (error) {
    console.error('Error fetching auth config:', error);
    throw error;
  }
}
class GrafanaApiService {
  constructor() {
    this.cache = new Map();
    this.apiBaseUrl = null; //will be set asynchronously
    this.isLoading = false;
    this.lastFetchTime = null;
    this.initPromise = null; //track initialization 
  }

  async initialize() {
    if (this.apiBaseUrl) {
      return this.apiBaseUrl;
    }

    if (this.initPromise) {
      return this.initPromise;
    }

    this.initPromise = (async () => {
      try {
        this.apiBaseUrl = await fetchUrlConfig();
        return this.apiBaseUrl;
      } catch (error) {
        console.error('Error during initialization:', error);
        this.initPromise = null; //reset iso it can be retried
        throw error;
      }
    })();
    return this.initPromise;
  }
  //API endpoint for Grafana dashboards
  async getGrafanaManifestUrl() {
    await this.initialize(); //ensure apiBaseUrl is set
    return `${this.apiBaseUrl}/api/v1/grafana/dashboards/manifest`;
  }

  //Utility function to add delay for retry logic
  delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Check if cached data is still valid
   */
  isCacheValid(cacheKey) {
    const cached = this.cache.get(cacheKey);
    if (!cached) return false;

    const now = Date.now();
    return (now - cached.timestamp) < CACHE_DURATION;
  }

  /**
   * Fetch Grafana dashboard manifest with retry logic
   */
  async fetchWithRetry(url, options = {}, retryCount = 0) {
    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...options.headers
        },
        ...options
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();

      // Validate response structure
      if (!data || data.status !== 'SUCCESS' || !data.data) {
        throw new Error('Invalid API response structure');
      }

      return data;
    } catch (error) {
      console.error(`Attempt ${retryCount + 1} failed:`, error.message);

      if (retryCount < MAX_RETRIES) {
        await this.delay(RETRY_DELAY * Math.pow(2, retryCount)); // Exponential backoff
        return this.fetchWithRetry(url, options, retryCount + 1);
      }

      throw error;
    }
  }

  /**
   * Get Grafana dashboard manifest from API
   */
  async getDashboardManifest(idToken, forceRefresh = false) {
    await this.initialize(); //ensure apiBaseUrl is set
    const cacheKey = 'grafana_manifest';

    // Return cached data if valid and not forcing refresh
    if (!forceRefresh && this.isCacheValid(cacheKey)) {
      return this.cache.get(cacheKey).data;
    }

    // Prevent multiple simultaneous API calls
    if (this.isLoading) {
      // Wait for existing request to complete
      while (this.isLoading) {
        await this.delay(100);
      }
      // Return cached result if available
      if (this.isCacheValid(cacheKey)) {
        return this.cache.get(cacheKey).data;
      }
    }

    if (!idToken) {
      throw new Error('ID token is required for Grafana API authentication');
    }

    try {
      this.isLoading = true;

      const url = await this.getGrafanaManifestUrl();
      const response = await this.fetchWithRetry(url, {
        headers: {
          'Authorization': `Bearer ${idToken}`
        }
      });

      // Cache the successful response
      this.cache.set(cacheKey, {
        data: response.data,
        timestamp: Date.now()
      });

      this.lastFetchTime = Date.now();

      return response.data;
    } catch (error) {
      console.error('Failed to fetch Grafana dashboard manifest:', error);

      // Return cached data if available, even if expired
      const cached = this.cache.get(cacheKey);
      if (cached) {
        console.warn('Returning stale cached data due to API failure');
        return cached.data;
      }

      throw error;
    } finally {
      this.isLoading = false;
    }
  }

  /**
   * Get dashboard URL for a specific service
   */
  async getDashboardUrl(serviceKey, idToken) {
    if (!idToken) {
      throw new Error('ID token is required for dashboard URL generation');
    }

    try {
      const manifest = await this.getDashboardManifest(idToken);

      if (!manifest?.dashboardUrls) {
        throw new Error('Dashboard URLs not available in manifest');
      }

      // service keys mapped to API keys
      const serviceToApiKeyMap = {
        'service-health': 'serviceHealth',
        'cases': 'incidents',
        'changes': 'changes',
        'sla-reporting': 'serviceLevelAgreementAndReporting',
        'environment-performance': 'serviceRequests',
        'problems-rca': 'problemsAndRootCauseAnalysis',
        'customer-management': 'customerServiceManagement'
      };

      const apiKey = serviceToApiKeyMap[serviceKey];
      if (!apiKey) {
        throw new Error(`This service is not available: ${serviceKey}`);
      }

      const dashboardUrl = manifest.dashboardUrls[apiKey];
      if (!dashboardUrl) {
        throw new Error(`Dashboard URL not found for service: ${serviceKey} (API key: ${apiKey})`);
      }

      // Add auth token for embedding - preserve existing kiosk configuration from backend
      const url = new URL(dashboardUrl);

      // Add ID token as auth_token parameter if not already present
      if (idToken && !url.searchParams.has('auth_token')) {
        url.searchParams.set('auth_token', idToken);
      }

      const finalUrl = url.toString();
      return finalUrl;
    } catch (error) {
      console.error(`Error getting dashboard URL for ${serviceKey}:`, error);
      throw error;
    }
  }

  /**
   * Get organization ID from manifest
   */
  async getOrganizationId(idToken) {
    try {
      const manifest = await this.getDashboardManifest(idToken);
      return manifest.organizationId || 1;
    } catch (error) {
      console.error('Error getting organization ID:', error);
      return 1; // Default fallback
    }
  }

  /**
   * Clear cache 
   */
  clearCache() {
    this.cache.clear();
    this.lastFetchTime = null;
  }

  /**
   * Get cache statistics for debugging
   */
  getCacheStats() {
    return {
      cacheSize: this.cache.size,
      lastFetchTime: this.lastFetchTime,
      isLoading: this.isLoading,
      cacheEntries: Array.from(this.cache.keys())
    };
  }

  /**
   * Health check for the service
   */
  async healthCheck(idToken) {
    try {
      await this.getDashboardManifest(idToken, true); // Force refresh
      return {
        status: 'healthy',
        lastFetch: this.lastFetchTime,
        message: 'Grafana API service is working correctly'
      };
    } catch (error) {
      return {
        status: 'unhealthy',
        error: error.message,
        message: 'Failed to connect to Grafana API'
      };
    }
  }
}

// Create and export singleton instance
export const grafanaApiService = new GrafanaApiService();

// Export class for testing
export { GrafanaApiService };

// Utility function for components to get dashboard URLs
export const getGrafanaDashboardUrl = async (serviceKey, idToken) => {
  return await grafanaApiService.getDashboardUrl(serviceKey, idToken);
};

// Utility function to refresh dashboard manifest
export const refreshGrafanaManifest = async (idToken) => {
  return await grafanaApiService.getDashboardManifest(idToken, true);
};
